import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import {
  Search, Globe, FileText, BarChart3, Plus, Pencil, Trash2, Eye, ExternalLink,
  Save, RefreshCw, TrendingUp, MousePointerClick, Copy, Check
} from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

interface SeoSetting {
  id: string;
  page_path: string;
  title: string | null;
  description: string | null;
  og_title: string | null;
  og_description: string | null;
  og_image: string | null;
  canonical_url: string | null;
  keywords: string[] | null;
  schema_type: string | null;
  is_indexed: boolean;
  priority: number | null;
  change_frequency: string | null;
  updated_at: string;
}

interface SeoCampaign {
  id: string;
  slug: string;
  title: string;
  meta_title: string | null;
  meta_description: string | null;
  og_image: string | null;
  heading: string;
  subheading: string | null;
  body_content: string | null;
  keywords: string[] | null;
  target_segment: string | null;
  cta_text: string | null;
  cta_url: string | null;
  is_published: boolean;
  views_count: number;
  created_at: string;
  updated_at: string;
}

const AdminSeoTab = () => {
  const { toast } = useToast();
  const [settings, setSettings] = useState<SeoSetting[]>([]);
  const [campaigns, setCampaigns] = useState<SeoCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingSetting, setEditingSetting] = useState<SeoSetting | null>(null);
  const [editingCampaign, setEditingCampaign] = useState<SeoCampaign | null>(null);
  const [showSettingDialog, setShowSettingDialog] = useState(false);
  const [showCampaignDialog, setShowCampaignDialog] = useState(false);
  const [copiedSitemap, setCopiedSitemap] = useState(false);

  const fetchData = useCallback(async () => {
    const [settingsRes, campaignsRes] = await Promise.all([
      supabase.from("seo_settings").select("*").order("priority", { ascending: false }),
      supabase.from("seo_campaigns").select("*").order("views_count", { ascending: false }),
    ]);
    setSettings((settingsRes.data as any[]) || []);
    setCampaigns((campaignsRes.data as any[]) || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // -- SEO Settings CRUD --
  const saveSetting = async () => {
    if (!editingSetting) return;
    const { id, ...data } = editingSetting;
    if (id) {
      await supabase.from("seo_settings").update(data).eq("id", id);
    } else {
      await supabase.from("seo_settings").insert(data);
    }
    toast({ title: "Meta tags salvas!" });
    setShowSettingDialog(false);
    setEditingSetting(null);
    fetchData();
  };

  const deleteSetting = async (id: string) => {
    await supabase.from("seo_settings").delete().eq("id", id);
    toast({ title: "Página removida do SEO" });
    fetchData();
  };

  // -- SEO Campaigns CRUD --
  const saveCampaign = async () => {
    if (!editingCampaign) return;
    const { id, ...data } = editingCampaign;
    if (id) {
      await supabase.from("seo_campaigns").update(data).eq("id", id);
    } else {
      await supabase.from("seo_campaigns").insert(data);
    }
    toast({ title: "Campanha SEO salva!" });
    setShowCampaignDialog(false);
    setEditingCampaign(null);
    fetchData();
  };

  const deleteCampaign = async (id: string) => {
    await supabase.from("seo_campaigns").delete().eq("id", id);
    toast({ title: "Campanha removida" });
    fetchData();
  };

  const toggleCampaignPublished = async (campaign: SeoCampaign) => {
    await supabase.from("seo_campaigns").update({ is_published: !campaign.is_published }).eq("id", campaign.id);
    fetchData();
  };

  const sitemapUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sitemap`;

  const copySitemapUrl = () => {
    navigator.clipboard.writeText(sitemapUrl);
    setCopiedSitemap(true);
    setTimeout(() => setCopiedSitemap(false), 2000);
  };

  const newSetting = (): SeoSetting => ({
    id: "",
    page_path: "/",
    title: "",
    description: "",
    og_title: "",
    og_description: "",
    og_image: "",
    canonical_url: "",
    keywords: [],
    schema_type: "WebPage",
    is_indexed: true,
    priority: 0.5,
    change_frequency: "weekly",
    updated_at: new Date().toISOString(),
  });

  const newCampaign = (): SeoCampaign => ({
    id: "",
    slug: "",
    title: "",
    meta_title: "",
    meta_description: "",
    og_image: "",
    heading: "",
    subheading: "",
    body_content: "",
    keywords: [],
    target_segment: "geral",
    cta_text: "Comece Grátis",
    cta_url: "/cadastro",
    is_published: false,
    views_count: 0,
    created_at: "",
    updated_at: "",
  });

  const totalViews = campaigns.reduce((sum, c) => sum + (c.views_count || 0), 0);
  const publishedCount = campaigns.filter((c) => c.is_published).length;
  const indexedPages = settings.filter((s) => s.is_indexed).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-display font-bold flex items-center gap-2">
            <Search className="h-5 w-5 text-primary" /> SEO & Indexação
          </h2>
          <p className="text-sm text-muted-foreground">Gerencie meta tags, campanhas de palavras-chave e sitemap</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData}>
          <RefreshCw className="h-4 w-4 mr-1" /> Atualizar
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
              <Globe className="h-3.5 w-3.5" /> Páginas Indexadas
            </div>
            <p className="text-2xl font-bold">{indexedPages}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
              <FileText className="h-3.5 w-3.5" /> Campanhas Ativas
            </div>
            <p className="text-2xl font-bold">{publishedCount}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
              <Eye className="h-3.5 w-3.5" /> Total de Visualizações
            </div>
            <p className="text-2xl font-bold">{totalViews.toLocaleString("pt-BR")}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
              <TrendingUp className="h-3.5 w-3.5" /> Sitemap
            </div>
            <Button variant="ghost" size="sm" className="text-xs p-0 h-auto" onClick={copySitemapUrl}>
              {copiedSitemap ? <Check className="h-3 w-3 mr-1" /> : <Copy className="h-3 w-3 mr-1" />}
              {copiedSitemap ? "Copiado!" : "Copiar URL"}
            </Button>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="settings">
        <TabsList>
          <TabsTrigger value="settings">
            <Globe className="h-4 w-4 mr-1" /> Meta Tags ({settings.length})
          </TabsTrigger>
          <TabsTrigger value="campaigns">
            <FileText className="h-4 w-4 mr-1" /> Campanhas ({campaigns.length})
          </TabsTrigger>
          <TabsTrigger value="sitemap">
            <BarChart3 className="h-4 w-4 mr-1" /> Sitemap
          </TabsTrigger>
        </TabsList>

        {/* META TAGS TAB */}
        <TabsContent value="settings" className="space-y-4">
          <div className="flex justify-end">
            <Button size="sm" onClick={() => { setEditingSetting(newSetting()); setShowSettingDialog(true); }}>
              <Plus className="h-4 w-4 mr-1" /> Nova Página
            </Button>
          </div>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Página</TableHead>
                    <TableHead>Título SEO</TableHead>
                    <TableHead>Indexada</TableHead>
                    <TableHead>Prioridade</TableHead>
                    <TableHead className="w-24">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {settings.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell className="font-mono text-xs">{s.page_path}</TableCell>
                      <TableCell className="text-xs max-w-xs truncate">{s.title || "—"}</TableCell>
                      <TableCell>
                        <Badge variant={s.is_indexed ? "default" : "secondary"} className="text-[10px]">
                          {s.is_indexed ? "Sim" : "Não"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">{s.priority}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditingSetting(s); setShowSettingDialog(true); }}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => deleteSetting(s.id)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* CAMPAIGNS TAB */}
        <TabsContent value="campaigns" className="space-y-4">
          <div className="flex justify-end">
            <Button size="sm" onClick={() => { setEditingCampaign(newCampaign()); setShowCampaignDialog(true); }}>
              <Plus className="h-4 w-4 mr-1" /> Nova Campanha
            </Button>
          </div>
          <div className="grid gap-4">
            {campaigns.map((c) => (
              <Card key={c.id}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-sm truncate">{c.title}</h3>
                        <Badge variant={c.is_published ? "default" : "secondary"} className="text-[10px] shrink-0">
                          {c.is_published ? "Publicada" : "Rascunho"}
                        </Badge>
                        <Badge variant="outline" className="text-[10px] shrink-0">{c.target_segment}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{c.meta_description || c.subheading}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1"><Eye className="h-3 w-3" /> {c.views_count} views</span>
                        <span className="font-mono">/s/{c.slug}</span>
                      </div>
                      {c.keywords?.length ? (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {c.keywords.slice(0, 5).map((k) => (
                            <Badge key={k} variant="outline" className="text-[9px] font-normal">{k}</Badge>
                          ))}
                          {c.keywords.length > 5 && (
                            <Badge variant="outline" className="text-[9px] font-normal">+{c.keywords.length - 5}</Badge>
                          )}
                        </div>
                      ) : null}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => toggleCampaignPublished(c)}>
                        {c.is_published ? <Eye className="h-3.5 w-3.5" /> : <Globe className="h-3.5 w-3.5" />}
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditingCampaign(c); setShowCampaignDialog(true); }}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => deleteCampaign(c.id)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                      {c.is_published && (
                        <Button variant="ghost" size="icon" className="h-7 w-7" asChild>
                          <a href={`/s/${c.slug}`} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {campaigns.length === 0 && (
              <p className="text-center text-muted-foreground text-sm py-8">Nenhuma campanha SEO criada ainda.</p>
            )}
          </div>
        </TabsContent>

        {/* SITEMAP TAB */}
        <TabsContent value="sitemap" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Sitemap Dinâmico</CardTitle>
              <CardDescription>
                O sitemap é gerado automaticamente com todas as páginas indexadas, campanhas publicadas e cardápios de restaurantes ativos.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-xs">URL do Sitemap</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={sitemapUrl} readOnly className="font-mono text-xs" />
                  <Button variant="outline" size="sm" onClick={copySitemapUrl}>
                    {copiedSitemap ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <div className="bg-muted/50 rounded-lg p-4 text-xs space-y-2 text-muted-foreground">
                <p><strong>Como usar:</strong></p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Adicione a URL do sitemap ao <strong>robots.txt</strong> do seu domínio</li>
                  <li>Submeta o sitemap no <strong>Google Search Console</strong></li>
                  <li>Submeta também no <strong>Bing Webmaster Tools</strong></li>
                </ol>
                <p className="mt-3">O sitemap inclui:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li><strong>{indexedPages}</strong> páginas estáticas indexadas</li>
                  <li><strong>{publishedCount}</strong> campanhas SEO publicadas</li>
                  <li>Todos os cardápios de restaurantes ativos</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* SEO SETTING DIALOG */}
      <Dialog open={showSettingDialog} onOpenChange={setShowSettingDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingSetting?.id ? "Editar" : "Nova"} Página SEO</DialogTitle>
          </DialogHeader>
          {editingSetting && (
            <div className="space-y-4">
              <div>
                <Label>Caminho da página</Label>
                <Input placeholder="/" value={editingSetting.page_path} onChange={(e) => setEditingSetting({ ...editingSetting, page_path: e.target.value })} />
              </div>
              <div>
                <Label>Título SEO</Label>
                <Input placeholder="Título da página" value={editingSetting.title || ""} onChange={(e) => setEditingSetting({ ...editingSetting, title: e.target.value })} />
                <p className="text-[10px] text-muted-foreground mt-1">{(editingSetting.title || "").length}/60 caracteres</p>
              </div>
              <div>
                <Label>Descrição</Label>
                <Textarea placeholder="Meta description" value={editingSetting.description || ""} onChange={(e) => setEditingSetting({ ...editingSetting, description: e.target.value })} rows={3} />
                <p className="text-[10px] text-muted-foreground mt-1">{(editingSetting.description || "").length}/160 caracteres</p>
              </div>
              <div>
                <Label>OG Title</Label>
                <Input value={editingSetting.og_title || ""} onChange={(e) => setEditingSetting({ ...editingSetting, og_title: e.target.value })} />
              </div>
              <div>
                <Label>OG Description</Label>
                <Textarea value={editingSetting.og_description || ""} onChange={(e) => setEditingSetting({ ...editingSetting, og_description: e.target.value })} rows={2} />
              </div>
              <div>
                <Label>OG Image URL</Label>
                <Input value={editingSetting.og_image || ""} onChange={(e) => setEditingSetting({ ...editingSetting, og_image: e.target.value })} />
              </div>
              <div>
                <Label>Canonical URL</Label>
                <Input value={editingSetting.canonical_url || ""} onChange={(e) => setEditingSetting({ ...editingSetting, canonical_url: e.target.value })} />
              </div>
              <div>
                <Label>Keywords (separadas por vírgula)</Label>
                <Input value={(editingSetting.keywords || []).join(", ")} onChange={(e) => setEditingSetting({ ...editingSetting, keywords: e.target.value.split(",").map((k) => k.trim()).filter(Boolean) })} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Prioridade (0-1)</Label>
                  <Input type="number" min={0} max={1} step={0.1} value={editingSetting.priority || 0.5} onChange={(e) => setEditingSetting({ ...editingSetting, priority: parseFloat(e.target.value) })} />
                </div>
                <div>
                  <Label>Frequência</Label>
                  <Select value={editingSetting.change_frequency || "weekly"} onValueChange={(v) => setEditingSetting({ ...editingSetting, change_frequency: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="always">Sempre</SelectItem>
                      <SelectItem value="hourly">A cada hora</SelectItem>
                      <SelectItem value="daily">Diária</SelectItem>
                      <SelectItem value="weekly">Semanal</SelectItem>
                      <SelectItem value="monthly">Mensal</SelectItem>
                      <SelectItem value="yearly">Anual</SelectItem>
                      <SelectItem value="never">Nunca</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={editingSetting.is_indexed} onCheckedChange={(v) => setEditingSetting({ ...editingSetting, is_indexed: v })} />
                <Label>Indexar no sitemap</Label>
              </div>
              <Button className="w-full" onClick={saveSetting}>
                <Save className="h-4 w-4 mr-1" /> Salvar
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* CAMPAIGN DIALOG */}
      <Dialog open={showCampaignDialog} onOpenChange={setShowCampaignDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCampaign?.id ? "Editar" : "Nova"} Campanha SEO</DialogTitle>
          </DialogHeader>
          {editingCampaign && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Título interno</Label>
                  <Input value={editingCampaign.title} onChange={(e) => setEditingCampaign({ ...editingCampaign, title: e.target.value })} />
                </div>
                <div>
                  <Label>Slug (URL)</Label>
                  <Input placeholder="cardapio-digital-pizzaria" value={editingCampaign.slug} onChange={(e) => setEditingCampaign({ ...editingCampaign, slug: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Meta Title</Label>
                <Input value={editingCampaign.meta_title || ""} onChange={(e) => setEditingCampaign({ ...editingCampaign, meta_title: e.target.value })} />
                <p className="text-[10px] text-muted-foreground mt-1">{(editingCampaign.meta_title || "").length}/60 caracteres</p>
              </div>
              <div>
                <Label>Meta Description</Label>
                <Textarea value={editingCampaign.meta_description || ""} onChange={(e) => setEditingCampaign({ ...editingCampaign, meta_description: e.target.value })} rows={2} />
                <p className="text-[10px] text-muted-foreground mt-1">{(editingCampaign.meta_description || "").length}/160 caracteres</p>
              </div>
              <div>
                <Label>Heading (H1)</Label>
                <Input value={editingCampaign.heading} onChange={(e) => setEditingCampaign({ ...editingCampaign, heading: e.target.value })} />
              </div>
              <div>
                <Label>Subheading</Label>
                <Input value={editingCampaign.subheading || ""} onChange={(e) => setEditingCampaign({ ...editingCampaign, subheading: e.target.value })} />
              </div>
              <div>
                <Label>Conteúdo (HTML)</Label>
                <Textarea value={editingCampaign.body_content || ""} onChange={(e) => setEditingCampaign({ ...editingCampaign, body_content: e.target.value })} rows={8} className="font-mono text-xs" />
              </div>
              <div>
                <Label>Keywords (separadas por vírgula)</Label>
                <Input value={(editingCampaign.keywords || []).join(", ")} onChange={(e) => setEditingCampaign({ ...editingCampaign, keywords: e.target.value.split(",").map((k) => k.trim()).filter(Boolean) })} />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Segmento</Label>
                  <Input value={editingCampaign.target_segment || ""} onChange={(e) => setEditingCampaign({ ...editingCampaign, target_segment: e.target.value })} />
                </div>
                <div>
                  <Label>CTA Texto</Label>
                  <Input value={editingCampaign.cta_text || ""} onChange={(e) => setEditingCampaign({ ...editingCampaign, cta_text: e.target.value })} />
                </div>
                <div>
                  <Label>CTA URL</Label>
                  <Input value={editingCampaign.cta_url || ""} onChange={(e) => setEditingCampaign({ ...editingCampaign, cta_url: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>OG Image URL</Label>
                <Input value={editingCampaign.og_image || ""} onChange={(e) => setEditingCampaign({ ...editingCampaign, og_image: e.target.value })} />
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={editingCampaign.is_published} onCheckedChange={(v) => setEditingCampaign({ ...editingCampaign, is_published: v })} />
                <Label>Publicar campanha</Label>
              </div>
              <Button className="w-full" onClick={saveCampaign}>
                <Save className="h-4 w-4 mr-1" /> Salvar Campanha
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSeoTab;
